import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { json, preprocessFormData, zfd } from 'zod-form-data';
import {
  orderContext,
  type Order,
} from '~/app/features/order-middleware/orderMiddleware';
import { AppStore } from '~/stores/app';
import { GspClaimReason } from '~/utils/constants';
import { addBreadcrumb } from '~/utils/observability';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { mediaAssetSchema } from '~/utils/schemas';
import {
  shippingClaimContext,
  type ShippingProtectionClaim,
} from '../shippingClaimMiddleware';

const getRequiredImgUploadCount = ({
  order,
  shippingProtectionClaim,
}: {
  order: Order;
  shippingProtectionClaim: ShippingProtectionClaim;
}) =>
  Number(
    order.isShippingClaimImgRequired &&
      shippingProtectionClaim.reason === GspClaimReason.damaged,
  );

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const shippingProtectionClaim = context.get(shippingClaimContext);
  const order = context.get(orderContext);

  return match(shippingProtectionClaim.reason)
    .with(GspClaimReason.damaged, () => ({
      uploadFileInfo: {
        storefrontId: order.storefrontId,
        destination: 'gspClaimImg' as const,
        requiredImgUploadCount: getRequiredImgUploadCount({
          order,
          shippingProtectionClaim,
        }),
      },
      ...shippingProtectionClaim.meta,
    }))
    .with(P.nonNullable, () => ({
      uploadFileInfo: undefined,
      ...shippingProtectionClaim.meta,
    }))
    .otherwise(() => {
      addBreadcrumb({
        message:
          'Shipping protection claim reason is not defined at meta loader.',
        level: 'warning',
        data: { reason: shippingProtectionClaim.reason },
      });
      // back to the order page if the reason is not defined
      return redirect('../..');
    });
});

const makeFormSchema = (minAssets = 0) =>
  zfd.formData(
    z.object({
      noteFromCustomer: zfd.text(z.string().optional()),
      assets: zfd.repeatable(
        z.array(json(mediaAssetSchema)).min(minAssets, {
          message: `A minimum of ${minAssets} media files are required`,
        }),
      ),
    }),
  );

const handleFormDataError = (formData: FormData) => (error: z.ZodError) => {
  const minAssetsIssue = error.issues.find(
    (issue) => issue.path.includes('assets') && issue.code === 'too_small',
  );

  if (!minAssetsIssue) {
    throw new Error('Malformed form data', {
      cause: {
        error,
        formData: preprocessFormData(formData),
      },
    });
  }

  return {
    ok: false,
    message: minAssetsIssue.message,
  };
};

const handleFormData =
  (appStore: AppStore) =>
  ({
    noteFromCustomer,
    assets,
  }: z.infer<ReturnType<typeof makeFormSchema>>) => {
    appStore.dispatch(
      appStore.actions.shippingProtectionClaim.setMeta({
        noteFromCustomer,
        assets,
      }),
    );

    return redirect('../address');
  };

export const { action, useActionResult } = makeAction(
  ({ formData, context }, appStore) => {
    const order = context.get(orderContext);
    const shippingProtectionClaim = context.get(shippingClaimContext);

    return match(
      makeFormSchema(
        getRequiredImgUploadCount({ order, shippingProtectionClaim }),
      ).safeParse(formData),
    )
      .with({ success: true, data: P.select() }, handleFormData(appStore))
      .with(
        { success: false, error: P.select() },
        handleFormDataError(formData),
      )
      .exhaustive();
  },
);
